<template>
  <div class="mt-4 mx-auto player-notes">
    <v-card class="mb-4">
      <v-card-text>
        <v-textarea
          v-model="newNote"
          outlined
          dense
          auto-grow
          rows="4"
          hide-details
          placeholder="Describe what was done with the player (e.g. replaced SD card, moved to another location)..."
        />
      </v-card-text>
      <v-card-actions class="px-4 pb-4 pt-0">
        <v-spacer />
        <v-btn
          color="primary"
          :loading="saving"
          :disabled="!newNote || !newNote.trim()"
          @click="createNote()"
        >
          Add Note
        </v-btn>
      </v-card-actions>
    </v-card>

    <div
      v-if="loading"
      class="text-center pa-6"
    >
      <v-progress-circular
        color="primary"
        indeterminate
      />
    </div>
    <div
      v-else-if="notes.length === 0"
      class="subtitle-1 text-center pa-6 grey--text"
    >
      No notes yet.
    </div>
    <div v-else>
      <v-card
        v-for="note in notes"
        :key="note.id"
        class="mb-2"
      >
        <v-card-subtitle class="pb-0">
          <v-row
            no-gutters
            align="center"
          >
            <v-col>
              <strong v-if="note.user">{{ note.user.name }}</strong>
              <span
                v-else
                class="grey--text"
              >
                Unknown user
              </span>
              &middot; {{ $dayjs(note.created_at).format('DD.MM.YYYY. HH:mm') }}
              <v-tooltip
                v-if="note.updated_at !== note.created_at"
                top
              >
                <template v-slot:activator="{ on }">
                  <span
                    class="grey--text"
                    v-on="on"
                  >
                    (edited)
                  </span>
                </template>
                <span>Edited on {{ $dayjs(note.updated_at).format('DD.MM.YYYY. HH:mm') }}</span>
              </v-tooltip>
            </v-col>
            <v-col
              v-if="editing.id !== note.id"
              cols="auto"
            >
              <v-btn
                icon
                small
                color="grey"
                @click="startEdit(note)"
              >
                <v-icon x-small>
                  fa fa-pen
                </v-icon>
              </v-btn>
              <v-btn
                icon
                small
                color="grey"
                @click="deleteDialog.note = note; deleteDialog.visible = true"
              >
                <v-icon x-small>
                  fa fa-trash
                </v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-subtitle>
        <v-card-text
          v-if="editing.id === note.id"
          class="pt-3"
        >
          <v-textarea
            v-model="editing.body"
            outlined
            dense
            auto-grow
            rows="4"
            hide-details
          />
          <div class="text-right mt-3">
            <v-btn
              text
              class="mr-2"
              @click="cancelEdit()"
            >
              Cancel
            </v-btn>
            <v-btn
              color="primary"
              :loading="saving"
              :disabled="!editing.body || !editing.body.trim()"
              @click="updateNote()"
            >
              Save
            </v-btn>
          </div>
        </v-card-text>
        <v-card-text
          v-else
          class="pt-3 body-1"
        >
          <div
            class="note-body"
            v-text="note.body"
          />
        </v-card-text>
      </v-card>
    </div>

    <confirm-delete-dialog
      :visible.sync="deleteDialog.visible"
      :submitting="deleting"
      text="Are you sure you want to delete this note?"
      @confirm="deleteNote()"
    />
  </div>
</template>

<script>
import Players from '@/services/Players'
import ConfirmDeleteDialog from '@/components/helpers/ConfirmDeleteDialog'

export default {
  name: 'PlayerNotes',
  components: { ConfirmDeleteDialog },
  props: {
    player: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      notes: [],
      loading: false,
      saving: false,
      deleting: false,
      newNote: '',
      editing: {
        id: undefined,
        body: ''
      },
      deleteDialog: {
        visible: false,
        note: undefined
      }
    }
  },
  watch: {
    'notes.length': function (count) {
      this.$emit('notes:count', count)
    }
  },
  created() {
    this.fetchNotes()
  },
  methods: {
    async fetchNotes() {
      this.loading = true

      try {
        this.notes = await Players.fetchNotes(this.player.id)
      } finally {
        this.loading = false
      }
    },
    async createNote() {
      this.saving = true

      try {
        const note = await Players.createNote(this.player.id, { body: this.newNote })

        this.notes.unshift(note)
        this.newNote = ''
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'Something went wrong',
          color: 'error'
        })
        throw e
      } finally {
        this.saving = false
      }
    },
    startEdit(note) {
      this.editing.id = note.id
      this.editing.body = note.body
    },
    cancelEdit() {
      this.editing.id = undefined
      this.editing.body = ''
    },
    async updateNote() {
      this.saving = true

      try {
        const note = await Players.updateNote(this.player.id, this.editing.id, { body: this.editing.body })

        const index = this.notes.findIndex(n => n.id === note.id)
        if (index !== -1) this.notes.splice(index, 1, note)

        this.cancelEdit()
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'Something went wrong',
          color: 'error'
        })
        throw e
      } finally {
        this.saving = false
      }
    },
    async deleteNote() {
      this.deleting = true

      try {
        await Players.deleteNote(this.player.id, this.deleteDialog.note.id)

        this.notes = this.notes.filter(n => n.id !== this.deleteDialog.note.id)

        this.$store.dispatch('alert', {
          text: 'Note deleted',
          color: 'success'
        })
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'Something went wrong',
          color: 'error'
        })
        throw e
      } finally {
        this.deleting = false
        this.deleteDialog.visible = false
      }
    }
  }
}
</script>

<style scoped>
.player-notes {
  max-width: 800px;
}

.note-body {
  white-space: pre-wrap;
}
</style>
